import React, { useState } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Select, InputNumber, Alert, Descriptions, Drawer, Typography } from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  SettingOutlined,
  CloudSyncOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { Option } = Select;

const ABSoftSyncPanel = ({ companyId, syncHistory, onTriggerSync, onRetrySync, onViewDetails, isLoading, abSoftEnabled }) => {
  const { t } = useTranslation(['tables', 'forms', 'messages']);
  const pa = (key) => t(`payrollAutomation.${key}`, { ns: 'tables' });

  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [selectedSync, setSelectedSync] = useState(null);
  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();

  const SYNC_STATUS = {
    SUCCESS: { color: 'success', icon: <CheckCircleOutlined />, label: pa('statusCompleted') },
    FAILED: { color: 'error', icon: <CloseCircleOutlined />, label: pa('workflowFailed') },
    SYNCING: { color: 'processing', icon: <SyncOutlined spin />, label: pa('triggerSync') },
    PENDING: { color: 'default', icon: <ClockCircleOutlined />, label: pa('statusDraft') },
  };

  const SYNC_TYPES = [
    { value: 'FULL_SYNC', label: pa('fullSync') },
    { value: 'INCREMENTAL', label: pa('incrementalSync') },
    { value: 'EMPLOYEES_ONLY', label: pa('employeesOnly') },
    { value: 'SALARIES_ONLY', label: pa('timesheetsOnly') },
  ];

  const handleTriggerSync = () => {
    setSyncModalVisible(true);
    form.resetFields();
  };

  const handleSubmitSync = async () => {
    try {
      const values = await form.validateFields();
      await onTriggerSync({
        companyId,
        ...values,
      });
      setSyncModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Sync trigger failed:', error);
    }
  };

  const handleRetry = (syncId) => {
    Modal.confirm({
      title: pa('retry'),
      content: pa('deactivateCompanyDesc'),
      okText: pa('yes'),
      cancelText: pa('no'),
      onOk: async () => {
        await onRetrySync(syncId);
      },
    });
  };

  const handleViewDetails = (sync) => {
    setSelectedSync(sync);
    setDetailsDrawerVisible(true);
    if (onViewDetails) {
      onViewDetails(sync._id);
    }
  };

  const columns = [
    {
      title: pa('startedAt'),
      dataIndex: 'syncedAt',
      key: 'syncedAt',
      width: 180,
      render: (syncedAt) => (
        <div>
          <div>{dayjs(syncedAt).format('DD.MM.YYYY')}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(syncedAt).format('HH:mm:ss')}
          </Text>
        </div>
      ),
      sorter: (a, b) => dayjs(a.syncedAt).unix() - dayjs(b.syncedAt).unix(),
    },
    {
      title: pa('period'),
      key: 'period',
      render: (_, record) => (
        <Tag color="blue">
          {record.month}/{record.year}
        </Tag>
      ),
    },
    {
      title: pa('syncType'),
      dataIndex: 'syncType',
      key: 'syncType',
      render: (syncType) => {
        const type = SYNC_TYPES.find(t => t.value === syncType);
        return <Tag>{type?.label || syncType}</Tag>;
      },
    },
    {
      title: pa('recordsProcessed'),
      key: 'recordsSynced',
      render: (_, record) => {
        const stats = record.syncStats || {};
        return (
          <div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>{pa('employees')}: </Text>
              <Text strong>{stats.employeesSynced || 0}</Text>
            </div>
          </div>
        );
      },
    },
    {
      title: pa('duration'),
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => {
        if (!duration) return '-';
        return `${(duration / 1000).toFixed(2)}s`;
      },
    },
    {
      title: t('status', { ns: 'tables' }),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const config = SYNC_STATUS[status] || SYNC_STATUS.PENDING;
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: t('actions', { ns: 'tables' }),
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            {pa('view')}
          </Button>
          {record.status === 'FAILED' && (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleRetry(record._id)}
            >
              {pa('retry')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (!abSoftEnabled) {
    return (
      <Card
        title={
          <Space>
            <CloudSyncOutlined />
            <span>{pa('abSoftIntegration')}</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Alert
          message={pa('abSoftNotEnabled')}
          description={pa('abSoftNotEnabledDesc')}
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => setSettingsModalVisible(true)}>
              {pa('configure')}
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Space>
            <CloudSyncOutlined />
            <span>{pa('abSoftSyncStatus')}</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              className="btn-secondary"
              icon={<SettingOutlined />}
              onClick={() => setSettingsModalVisible(true)}
            >
              {pa('settings')}
            </Button>
            <Button
              type="primary"
              className="btn-primary"
              icon={<SyncOutlined />}
              onClick={handleTriggerSync}
              loading={isLoading}
            >
              {pa('triggerSync')}
            </Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          dataSource={syncHistory}
          columns={columns}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
          size="small"
          loading={isLoading}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Trigger Sync Modal */}
      <Modal
        title={pa('triggerAbSoftSync')}
        open={syncModalVisible}
        onCancel={() => setSyncModalVisible(false)}
        width={500}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setSyncModalVisible(false)}>{pa('cancel')}</button>
            <button className="btn-primary" onClick={handleSubmitSync}>{pa('syncNow')}</button>
          </div>
        }
      >
        <Alert
          message={pa('manualSync')}
          description={pa('manualSyncDesc')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical">
          <Form.Item
            name="month"
            label={t('month', { ns: 'forms' })}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
            initialValue={dayjs().month() + 1}
          >
            <Select placeholder={t('month', { ns: 'forms' })}>
              {[...Array(12)].map((_, i) => (
                <Option key={i + 1} value={i + 1}>
                  {dayjs().month(i).format('MMMM')}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="year"
            label={t('year', { ns: 'forms' })}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
            initialValue={dayjs().year()}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={2020}
              max={2030}
            />
          </Form.Item>

          <Form.Item
            name="syncType"
            label={pa('syncType')}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
            initialValue="FULL_SYNC"
          >
            <Select>
              {SYNC_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Settings Modal */}
      <Modal
        title={pa('abSoftSettings')}
        open={settingsModalVisible}
        onCancel={() => setSettingsModalVisible(false)}
        width={600}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setSettingsModalVisible(false)}>{pa('cancel')}</button>
            <button className="btn-primary" onClick={() => settingsForm.validateFields().then(() => setSettingsModalVisible(false))}>{pa('save')}</button>
          </div>
        }
      >
        <Form form={settingsForm} layout="vertical">
          <Alert
            message={pa('abSoftSettings')}
            description={pa('abSoftSettingsDesc')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="apiUrl"
            label={pa('apiUrl')}
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <input type="text" className="ant-input" placeholder="https://api.absoft.rs/v1" />
          </Form.Item>

          <Form.Item
            name="apiKey"
            label="API Key"
            rules={[{ required: true, message: t('required', { ns: 'forms' }) }]}
          >
            <input type="password" className="ant-input" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Drawer */}
      <Drawer
        title={pa('workflowDetails')}
        placement="right"
        width={720}
        open={detailsDrawerVisible}
        onClose={() => setDetailsDrawerVisible(false)}
      >
        {selectedSync && (
          <div>
            {selectedSync.status === 'FAILED' && (
              <Alert
                message={pa('workflowFailed')}
                description={selectedSync.error?.message || pa('errorMessage')}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                action={
                  <Button size="small" danger onClick={() => handleRetry(selectedSync._id)}>
                    {pa('retry')}
                  </Button>
                }
              />
            )}

            {selectedSync.status === 'SUCCESS' && (
              <Alert
                message={pa('statusCompleted')}
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Descriptions title={pa('syncStatus')} bordered column={1} size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label={pa('syncId')}>
                {selectedSync._id}
              </Descriptions.Item>
              <Descriptions.Item label={pa('period')}>
                {selectedSync.month}/{selectedSync.year}
              </Descriptions.Item>
              <Descriptions.Item label={pa('syncType')}>
                <Tag>{SYNC_TYPES.find(t => t.value === selectedSync.syncType)?.label || selectedSync.syncType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('status', { ns: 'tables' })}>
                <Tag icon={SYNC_STATUS[selectedSync.status]?.icon} color={SYNC_STATUS[selectedSync.status]?.color}>
                  {SYNC_STATUS[selectedSync.status]?.label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={pa('startedAt')}>
                {dayjs(selectedSync.syncedAt).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label={pa('completedAt')}>
                {selectedSync.completedAt
                  ? dayjs(selectedSync.completedAt).format('DD.MM.YYYY HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={pa('duration')}>
                {selectedSync.duration ? `${(selectedSync.duration / 1000).toFixed(2)}s` : '-'}
              </Descriptions.Item>
            </Descriptions>

            {selectedSync.syncStats && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>{pa('recordsProcessed')}</Title>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label={pa('employees')}>
                    {selectedSync.syncStats.employeesSynced || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label={pa('recordsFailed')}>
                    {selectedSync.syncStats.employeesFailed || 0}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}

            {selectedSync.error && (
              <div>
                <Title level={5}>{pa('errorMessage')}</Title>
                <Alert
                  message={selectedSync.error.message}
                  type="error"
                  showIcon
                />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default ABSoftSyncPanel;
